import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuthState } from '@/auth/AuthProvider';
import { getInvite, acceptMemberInvite, declineInvite } from '@/lib/onboarding';
import { acceptEvaluatorInvite, getScopeLabel } from '@/lib/share';
import LoadingSpinner from '@/components/LoadingSpinner';
import toast from 'react-hot-toast';

interface InviteDetails {
  id: string;
  caseId: string;
  inviterUid: string;
  inviterName?: string;
  inviteeEmail: string;
  role: string;
  scopes?: string[];
  status: string;
  createdAt: any;
  expiresAt?: any;
}

export default function InviteAcceptPage() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthState();

  const [invite, setInvite]       = useState<InviteDetails | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!inviteId || authLoading) return;

    async function loadInvite() {
      try {
        const inviteData = await getInvite(inviteId!);
        if (!inviteData) {
          setError('Invite not found or has expired');
        } else {
          setInvite(inviteData as InviteDetails);
        }
      } catch (err) {
        console.error('[InviteAcceptPage] Error loading invite:', err);
        setError('Failed to load invite details');
      } finally {
        setLoading(false);
      }
    }

    loadInvite();
  }, [inviteId, authLoading]);

  const handleAccept = async () => {
    if (!user || !invite) return;
    setProcessing(true);
    setError(null);

    try {
      if (invite.role === 'evaluator') {
        // Server-side CF: writes VIEW_ONLY member doc with scope permissions
        const result = await acceptEvaluatorInvite(invite.id);
        toast.success('Access granted! Taking you to the case…');
        setTimeout(() => navigate(result.redirectPath), 1200);
      } else {
        // Standard member invite (editor/member role) — handled via Cloud Function
        // so the Admin SDK can write to cases/{caseId} and members/{uid} before
        // the invitee has been added to memberIds.
        const result = await acceptMemberInvite(invite.id);
        toast.success('Invite accepted! Redirecting to case…');
        setTimeout(() => navigate(result.redirectPath), 1500);
      }
    } catch (err) {
      console.error('[InviteAcceptPage] Error accepting invite:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while accepting the invite');
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!user || !invite) return;
    setProcessing(true);
    setError(null);

    try {
      const result = await declineInvite(invite.id, user.uid, user.email!);
      if (result.success) {
        toast.success('Invite declined');
        setTimeout(() => navigate('/cases'), 1500);
      } else {
        setError(result.error ?? 'Failed to decline invite');
        setProcessing(false);
      }
    } catch (err) {
      console.error('[InviteAcceptPage] Error declining invite:', err);
      setError('An error occurred while declining the invite');
      setProcessing(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────
  if (loading || authLoading) {
    return (
      <div data-testid="invite.page.loading" className="min-h-screen flex items-center justify-center bg-slate-950">
        <LoadingSpinner />
      </div>
    );
  }

  // ── Error (no invite found) ───────────────────────────────────────────
  if (error && !invite) {
    return (
      <div data-testid="invite.page.error" className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl border border-slate-600 p-8 text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invite Not Available</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/cases')}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
          >
            Go to My Cases
          </button>
        </div>
      </div>
    );
  }

  // ── Not logged in ─────────────────────────────────────────────────────
  // Show options: log in OR sign up (new users must create account first)
  if (!user) {
    const isEvaluator = invite?.role === 'evaluator';
    return (
      <div data-testid="invite.page.notLoggedIn" className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl border border-slate-600 p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10">
            <EnvelopeIcon className="h-9 w-9 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Case Access Invitation</h1>

          {invite && (
            <div className="my-4 rounded-lg border border-slate-600 bg-slate-900 p-4 text-left">
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-white">{invite.inviterName ?? 'Someone'}</span> invited you to access a case
                {isEvaluator && invite.scopes && invite.scopes.length > 0 && (
                  <> with read-only access to: <span className="text-orange-400">{invite.scopes.map(s => getScopeLabel(s as any)).join(', ')}</span></>
                )}.
              </p>
            </div>
          )}

          <p className="text-slate-400 mb-6 text-sm">
            To accept this invitation you need a ThreadLock account.
            {isEvaluator && ' New to ThreadLock? You\'ll need to create an account first — it only takes a minute.'}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate(`/login?redirect=/invites/${inviteId}`)}
              className="w-full px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
            >
              Log In to Accept
            </button>
            <Link
              to="/signup"
              className="block w-full px-6 py-2.5 bg-slate-700 text-slate-100 rounded-lg hover:bg-slate-600 transition-colors font-medium text-center"
            >
              Create an Account
            </Link>
          </div>

          {isEvaluator && (
            <div className="mt-5 rounded-lg bg-slate-900/60 border border-slate-700 p-4 text-left text-xs text-slate-400">
              <div className="flex items-start gap-2">
                <LockClosedIcon className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <p>
                  <strong className="text-slate-300">New to ThreadLock?</strong> Create your account — once it's active, return to this email and click the invitation link again. Your invitation will be waiting in your notifications.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Already accepted ──────────────────────────────────────────────────
  if (invite?.status === 'accepted') {
    return (
      <div data-testid="invite.page.alreadyAccepted" className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl border border-slate-600 p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invite Already Accepted</h1>
          <p className="text-slate-400 mb-6">You've already accepted this invitation.</p>
          <button
            onClick={() => navigate(`/cases/${invite.caseId}`)}
            className="px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
          >
            Go to Case
          </button>
        </div>
      </div>
    );
  }

  // ── Declined / revoked ────────────────────────────────────────────────
  if (invite?.status === 'declined' || invite?.status === 'revoked') {
    return (
      <div data-testid="invite.page.alreadyDeclined" className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md w-full bg-slate-800 rounded-xl border border-slate-600 p-8 text-center">
          <XCircleIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            {invite.status === 'revoked' ? 'Invitation Revoked' : 'Invite Declined'}
          </h1>
          <p className="text-slate-400 mb-6">
            {invite.status === 'revoked'
              ? 'This invitation has been revoked by the case owner.'
              : "You've already declined this invitation."}
          </p>
          <button
            onClick={() => navigate('/cases')}
            className="px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
          >
            Go to My Cases
          </button>
        </div>
      </div>
    );
  }

  // ── Pending — show accept / decline ──────────────────────────────────
  const isEvaluator = invite?.role === 'evaluator';

  return (
    <div data-testid="invite.page.pending" className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
      <div className="max-w-md w-full bg-slate-800 rounded-xl border border-slate-600 p-8">
        <div className="text-center mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-500/10">
            <EnvelopeIcon className="h-9 w-9 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Case Access Invitation</h1>
          <p className="text-slate-400 text-sm">
            {invite?.inviterName ?? 'Someone'} invited you to{' '}
            {isEvaluator ? 'review' : 'collaborate on'} a case.
          </p>
        </div>

        {isEvaluator && invite?.scopes && invite.scopes.length > 0 && (
          <div className="mb-5 rounded-lg border border-slate-600 bg-slate-900 p-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Access includes</p>
            <div className="flex flex-wrap gap-2">
              {invite.scopes.map(scope => (
                <span key={scope} className="rounded-full bg-orange-500/20 px-3 py-1 text-xs text-orange-400 font-medium">
                  {getScopeLabel(scope as any)}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">This is read-only access. You cannot make changes to the case.</p>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={processing}
            data-testid="invite.page.accept"
            className="w-full rounded-lg bg-orange-600 px-6 py-2.5 font-medium text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
          >
            {processing ? 'Accepting…' : 'Accept Invitation'}
          </button>

          {!isEvaluator && (
            <button
              onClick={handleDecline}
              disabled={processing}
              data-testid="invite.page.decline"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-6 py-2.5 font-medium text-slate-300 hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              Decline
            </button>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Logged in as <span className="text-slate-300">{user?.email}</span>.{' '}
          <button onClick={() => navigate('/cases')} className="text-orange-400 hover:underline">
            Not you?
          </button>
        </p>
      </div>
    </div>
  );
}
